import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Search, Filter, MapPin, Gift } from 'lucide-react';
import Header from '../components/Header';
import GiveawayCard from '../components/GiveawayCard';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Giveaways = () => {
  const [giveaways, setGiveaways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [locationFilter, setLocationFilter] = useState('all');

  useEffect(() => {
    fetchGiveaways();
  }, [statusFilter, locationFilter]);

  const fetchGiveaways = async () => {
    setLoading(true);
    try {
      let url = `${API}/giveaways?limit=50`;
      if (statusFilter && statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (locationFilter && locationFilter !== 'all') url += `&province_state=${locationFilter}`;
      
      const response = await axios.get(url);
      setGiveaways(response.data.giveaways || []);
    } catch (error) {
      console.error('Error fetching giveaways:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredGiveaways = giveaways.filter(g => 
    g.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const provinces = ['Ontario', 'British Columbia', 'Alberta', 'Quebec', 'Manitoba', 'Saskatchewan'];

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 font-medium px-4 py-2 rounded-full text-sm mb-6">
              <Gift className="w-4 h-4" />
              All entries are 100% free
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              Browse Giveaways
            </h1>
            <p className="text-lg text-neutral-600">
              Find home upgrade giveaways near you. Enter for free and win amazing prizes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-white border-b border-neutral-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <Input
                placeholder="Search giveaways..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 rounded-xl"
                data-testid="search-input"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40 h-12 rounded-xl" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="ended">Ended</SelectItem>
                <SelectItem value="fulfilled">Fulfilled</SelectItem>
              </SelectContent>
            </Select>

            {/* Location Filter */}
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-full sm:w-48 h-12 rounded-xl" data-testid="location-filter">
                <MapPin className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {provinces.map(province => (
                  <SelectItem key={province} value={province}>{province}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Giveaways Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className="bg-white rounded-2xl h-80 animate-pulse" />
              ))}
            </div>
          ) : filteredGiveaways.length > 0 ? (
            <>
              <p className="text-neutral-600 mb-6">{filteredGiveaways.length} giveaways found</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="giveaways-list">
                {filteredGiveaways.map((giveaway, index) => (
                  <motion.div
                    key={giveaway.giveaway_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <GiveawayCard giveaway={giveaway} />
                  </motion.div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <Gift className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-neutral-600 mb-2">No Giveaways Found</h3>
              <p className="text-neutral-500">Try adjusting your filters or check back later.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Giveaways;
